import { useState, useEffect } from 'react';
import { db, storage } from './firebase';
import { collection, doc, setDoc, updateDoc, getDocs, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const KioskMenuManagement = ({ user }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [newItem, setNewItem] = useState({
    name: '',
    category: '',
    description: '',
    price: '',
    imageURL: '',
    available: true
  });
  const [editItemId, setEditItemId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterAvailable, setFilterAvailable] = useState('all');

  useEffect(() => {
    if (user) {
      fetchMenuItems();
    }
  }, [user]);

  useEffect(() => {
    let items = [...menuItems];
    // Filter
    if (filterCategory) {
      items = items.filter(item => item.category === filterCategory);
    }
    if (filterAvailable !== 'all') {
      const isAvailable = filterAvailable === 'available';
      items = items.filter(item => item.available === isAvailable);
    }
    // Sort
    items.sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'price') return a.price - b.price;
      return 0;
    });
    setFilteredItems(items);
  }, [menuItems, sortBy, filterCategory, filterAvailable]);

  const fetchMenuItems = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'menu'));
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        price: parseFloat(doc.data().price) || 0
      }));
      setMenuItems(items);
    } catch (error) {
      console.error('Error fetching menu items:', error);
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const saveItem = async () => {
    if (!user || !user.email.endsWith('@kactuskwenchers.com')) {
      alert('Unauthorized');
      return;
    }
    let imageURL = newItem.imageURL;
    if (imageFile) {
      const imageRef = ref(storage, `menuImages/${newItem.name.toLowerCase().replace(/\s/g, '_')}_${Date.now()}`);
      await uploadBytes(imageRef, imageFile);
      imageURL = await getDownloadURL(imageRef);
    }
    const itemData = {
      name: newItem.name,
      category: newItem.category,
      description: newItem.description,
      price: parseFloat(newItem.price) || 0,
      imageURL: imageURL,
      available: newItem.available,
      updatedAt: new Date()
    };
    try {
      if (editItemId) {
        await updateDoc(doc(db, 'menu', editItemId), itemData);
        setEditItemId(null);
      } else {
        await setDoc(doc(db, 'menu', newItem.name.toLowerCase().replace(/\s/g, '_')), itemData);
      }
      setNewItem({
        name: '',
        category: '',
        description: '',
        price: '',
        imageURL: '',
        available: true
      });
      setImageFile(null);
      fetchMenuItems();
    } catch (error) {
      console.error('Error saving menu item:', error);
      alert('Failed to save item: ' + error.message);
    }
  };

  const editItem = (item) => {
    setEditItemId(item.id);
    setNewItem({
      name: item.name,
      category: item.category,
      description: item.description,
      price: item.price.toString(),
      imageURL: item.imageURL,
      available: item.available
    });
  };

  const deleteItem = async (itemId) => {
    if (!user || !user.email.endsWith('@kactuskwenchers.com')) {
      alert('Unauthorized');
      return;
    }
    try {
      await deleteDoc(doc(db, 'menu', itemId));
      fetchMenuItems();
    } catch (error) {
      console.error('Error deleting menu item:', error);
      alert('Failed to delete item: ' + error.message);
    }
  };

  const categories = [...new Set(menuItems.map(item => item.category))];

  return (
    <div>
      <h2>Kiosk Menu Management</h2>
      <div className="inventory-section">
        <h3>{editItemId ? 'Edit Menu Item' : 'Add New Menu Item'}</h3>
        <div className="inventory-form">
          <input
            value={newItem.name}
            onChange={e => setNewItem({ ...newItem, name: e.target.value })}
            placeholder="Item Name"
          />
          <input
            value={newItem.category}
            onChange={e => setNewItem({ ...newItem, category: e.target.value })}
            placeholder="Category (e.g., Kactus_kolas)"
          />
          <input
            value={newItem.description}
            onChange={e => setNewItem({ ...newItem, description: e.target.value })}
            placeholder="Description"
          />
          <input
            value={newItem.price}
            onChange={e => setNewItem({ ...newItem, price: e.target.value })}
            placeholder="Price ($)"
            type="number"
            step="0.01"
          />
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
          {newItem.imageURL && (
            <p>Current Image: <a href={newItem.imageURL} target="_blank" rel="noopener noreferrer">View</a></p>
          )}
          <div className="checkbox-container">
            <label>Available:</label>
            <input
              type="checkbox"
              checked={newItem.available}
              onChange={e => setNewItem({ ...newItem, available: e.target.checked })}
            />
          </div>
        </div>
        <div className="inventory-form-buttons">
          <button className="save-button" onClick={saveItem}>
            {editItemId ? 'Update' : 'Add'} Item
          </button>
          {editItemId && (
            <button
              className="cancel-button"
              onClick={() => {
                setEditItemId(null);
                setNewItem({
                  name: '',
                  category: '',
                  description: '',
                  price: '',
                  imageURL: '',
                  available: true
                });
                setImageFile(null);
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
      <h3>Menu Items</h3>
      <div className="filter-section">
        <select onChange={e => setSortBy(e.target.value)} value={sortBy}>
          <option value="name">Sort by Name</option>
          <option value="price">Sort by Price</option>
        </select>
        <select onChange={e => setFilterCategory(e.target.value)} value={filterCategory}>
          <option value="">All Categories</option>
          {categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
        <select onChange={e => setFilterAvailable(e.target.value)} value={filterAvailable}>
          <option value="all">All Availability</option>
          <option value="available">Available</option>
          <option value="unavailable">Unavailable</option>
        </select>
      </div>
      {filteredItems.length === 0 ? (
        <p>No menu items found.</p>
      ) : (
        <div className="inventory-grid">
          {filteredItems.map(item => (
            <div key={item.id} className="inventory-card">
              <h4>{item.name}</h4>
              <p>Category: {item.category || 'N/A'}</p>
              <p>Description: {item.description || 'N/A'}</p>
              <p>Price: ${item.price ? item.price.toFixed(2) : '0.00'}</p>
              <p>Image: {item.imageURL ? <img src={item.imageURL} alt={item.name} style={{ width: '100px' }} /> : 'N/A'}</p>
              <p>Status: {item.available ? 'Available' : 'Unavailable'}</p>
              <div className="actions">
                <button className="edit-button" onClick={() => editItem(item)}>
                  Edit
                </button>
                <button className="delete-button" onClick={() => deleteItem(item.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KioskMenuManagement;